import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import TooltipHost from '../components/charts/Tooltip';
import Donut from '../components/charts/Donut';
import BarChart from '../components/charts/BarChart';
import LineChart from '../components/charts/LineChart';
import Radar from '../components/charts/Radar';
import Sankey from '../components/charts/Sankey';
import Heatmap from '../components/charts/Heatmap';
import Gauge from '../components/charts/Gauge';
import CountUp from '../components/charts/CountUp';
import { SlideContent } from '../components/synthesis/SynthPresentation';
import { ThemeId, themeClass } from '../components/synthesis/themes';

type Share = {
  kind: 'pilotage' | 'presentation' | 'composition';
  title: string | null;
  project_name: string;
  theme_color: string;
  author_name: string | null;
  expires_at: string | null;
  payload: any;
};

export default function SharedView() {
  const { token } = useParams<{ token: string }>();
  const [share, setShare] = useState<Share | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    fetch(`/api/v1/share/${token}`)
      .then((r) => r.json())
      .then((d: any) => {
        if (d.error) setErr(d.error);
        else setShare(d.share);
      })
      .catch((e) => setErr(String(e)));
  }, [token]);

  if (err)
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <h2>Partage indisponible</h2>
        <p>{err}</p>
      </div>
    );
  if (!share) return <div style={{ padding: 40, textAlign: 'center' }}>Chargement…</div>;

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto', paddingBottom: 60 }}>
      <TooltipHost />
      <div
        style={{
          background: share.theme_color,
          color: '#fff',
          padding: '26px 32px',
          borderRadius: 14,
          marginBottom: 20,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 28 }}>{share.title ?? 'Synthèse'}</h1>
        <div style={{ opacity: 0.9, fontSize: 14, marginTop: 4 }}>
          Projet {share.project_name}
        </div>
      </div>

      {share.kind === 'pilotage' && <PublicPilotage payload={share.payload} />}
      {share.kind === 'presentation' && <PublicPresentation payload={share.payload} />}

      <div className="public-banner">
        Présentation partagée{share.author_name ? ` par ${share.author_name}` : ''} — Projet{' '}
        {share.project_name}
        {share.expires_at && ` · expire le ${new Date(share.expires_at).toLocaleDateString('fr-CH')}`}
      </div>
    </div>
  );
}

function PublicPilotage({ payload }: { payload: any }) {
  const m = payload?.metrics;
  if (!m) return <div>Données indisponibles.</div>;
  const stats = m.stats ?? {};
  const statusCounts: Record<string, number> = m.statusCounts ?? {};
  const radar = Array.isArray(m.radar) ? m.radar : [];
  const weekly = Array.isArray(m.weekly) ? m.weekly : [];
  const sankey = Array.isArray(m.sankey) ? m.sankey : [];
  const heatmap = Array.isArray(m.heatmap) ? m.heatmap : [];
  const perAssignee = Array.isArray(m.perAssignee) ? m.perAssignee : [];
  const checklistRatio = typeof m.checklist?.ratio === 'number' ? m.checklist.ratio : 0;
  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))',
          gap: 12,
          marginBottom: 18,
        }}
      >
        {Object.entries(stats).map(([k, v]) => (
          <div key={k} className="stat-card">
            <div className="lbl">{k.replace(/_/g, ' ')}</div>
            <div className="val">
              <CountUp value={Number(v)} />
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 14,
          marginBottom: 18,
        }}
      >
        <div className="pilot-card">
          <div className="pc-head">
            <span className="pc-title">Documents par statut</span>
          </div>
          <div className="pc-chart-wrap">
            <Donut
              data={Object.entries(statusCounts).map(([k, v]) => ({
                key: k,
                label: k,
                value: Number(v),
              }))}
            />
          </div>
        </div>
        <div className="pilot-card">
          <div className="pc-head">
            <span className="pc-title">KPIs projet</span>
          </div>
          <div className="pc-chart-wrap">
            <Radar data={radar} max={100} />
          </div>
        </div>
        <div className="pilot-card">
          <div className="pc-head">
            <span className="pc-title">Activité hebdomadaire</span>
          </div>
          <div className="pc-chart-wrap">
            <LineChart data={weekly} xKey="wk" yKey="n" />
          </div>
        </div>
        <div className="pilot-card">
          <div className="pc-head">
            <span className="pc-title">Flux documents</span>
          </div>
          <div className="pc-chart-wrap">
            <Sankey links={sankey} />
          </div>
        </div>
        <div className="pilot-card" style={{ gridColumn: 'span 2' }}>
          <div className="pc-head">
            <span className="pc-title">Heatmap contributions</span>
          </div>
          <div className="pc-chart-wrap" style={{ overflowX: 'auto' }}>
            <Heatmap data={heatmap} />
          </div>
        </div>
        <div className="pilot-card">
          <div className="pc-head">
            <span className="pc-title">Tâches par membre</span>
          </div>
          <div className="pc-chart-wrap">
            <BarChart
              data={perAssignee.map((p: any) => ({
                label: p?.name ?? '—',
                open: Number(p?.open ?? 0),
                done: Number(p?.done ?? 0),
              }))}
              series={[
                { key: 'open', label: 'Ouvertes', color: '#E07A5F' },
                { key: 'done', label: 'Terminées', color: '#8CB369' },
              ]}
              stacked
            />
          </div>
        </div>
        <div className="pilot-card">
          <div className="pc-head">
            <span className="pc-title">Avancement</span>
          </div>
          <div className="pc-chart-wrap">
            <Gauge value={Math.round(checklistRatio * 100)} label="validés / total" />
          </div>
        </div>
      </div>
      {payload.narrative && (
        <div className="card" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
          <h2 style={{ marginTop: 0 }}>Récit</h2>
          {payload.narrative}
        </div>
      )}
    </>
  );
}

function PublicPresentation({ payload }: { payload: any }) {
  const p = payload.presentation;
  if (!p) return <div>Présentation indisponible.</div>;
  const theme: ThemeId = (p.theme as ThemeId) ?? 'corporate';
  return (
    <div className={`pres-theme-wrap ${themeClass(theme)}`}>
      <div className="presentation-slides">
        {(p.sections ?? []).map((s: any, i: number) => (
          <div
            key={s.id ?? i}
            className={`presentation-slide ${s.type === 'hero' ? 'slide-hero' : ''}`}
            data-section-idx={String(i + 1).padStart(2, '0')}
          >
            <SlideContent section={s} />
          </div>
        ))}
      </div>
    </div>
  );
}
